import { Center } from "@/components/Center/Center";
import { Button } from "@/components/ui/button";
import { useHandleError } from "@/hooks/useHandleError";
import { useLineBrowserRedirect } from "@/hooks/useLineBrowserRedirect";
import { useDisplayLegalNotice } from "@/store";
import { useGoogleLogin } from "@react-oauth/google";
import LoadingSpinner from "@roman/shared/components/LoadingSpinner";
import { useLoginWithGoogle } from "@roman/shared/hooks/auth/useLoginWithGoogle";
import { isUnknownAuthState, useAuthStore } from "@roman/shared/store/auth.store";
import type { GoogleLoginCallbackArgs } from "@roman/shared/types/Google";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

export const GoogleAuthComponent = ({ text }: { text: string }) => {
  const { trigger: triggerLoginWithGoogle } = useLoginWithGoogle(useHandleError);
  const loginWithGoogle = useAuthStore((state) => state.loginWithGoogle);
  const { setShowLegalNotice, setIsPreventedNotice } = useDisplayLegalNotice();

  // useGoogleLogin can only be called inside a component with callback functions
  const handleLogin = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      const code = codeResponse.code;
      setShowLegalNotice(true);
      setIsPreventedNotice(false);
      const trigger = async ({ onSuccess, onError }: GoogleLoginCallbackArgs) => {
        await triggerLoginWithGoogle(
          { code },
          {
            onSuccess,
            onError,
          },
        );
      };

      await loginWithGoogle(trigger, toast);
    },
    onError: () => {
      toast.error("Login failed");
    },
    flow: "auth-code",
    scope: "email profile",
  });

  useLineBrowserRedirect();

  return (
    <Button
      variant="outline"
      onClick={handleLogin}
      className="flex items-center w-full sm:w-10/12 md:w-9/12 px-4 py-6 rounded-lg border-primary"
    >
      <img src="https://www.google.com/favicon.ico" alt="Google" className="w-[25px] h-[25px] mr-auto" />
      <span className="font-bold flex-grow text-center text-base text-primary">{text}</span>
      <div className="w-5" />
    </Button>
  );
};

export const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const authState = useAuthStore((state) => state.authState);
  const isNewUser = useAuthStore((state) => state.isNewUser);

  if (isUnknownAuthState(authState)) {
    return (
      <Center>
        <LoadingSpinner />
      </Center>
    );
  }

  if (authState === "authenticated") {
    if (isNewUser) navigate("/edit-profile");
    navigate(location.state?.from ?? "/");
  }

  return (
    <div className="w-full max-w-[770px] min-h-[calc(100vh-72px)] bg-white flex justify-center mx-auto">
      <div className="flex items-center justify-center">
        <div className="flex flex-col gap-10 w-full px-4 md:px-0">
          <h1 className="text-2xl font-semibold text-center text-black">ログイン</h1>

          <div className="flex justify-center">
            <GoogleAuthComponent text="Googleでログイン" />
          </div>
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t backgroundColor-gray" />
            </div>
          </div>
          <div className="space-y-4">
            <h1 className="text-base text-center text-black">アカウントをお持ちでない方は</h1>
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() => navigate("/register")}
                className="flex items-center w-full sm:w-10/12 md:w-9/12 px-4 py-6 rounded-lg border-primary"
              >
                <span className="font-bold w-full text-center text-primary">新規登録</span>
              </Button>
            </div>
          </div>
          <h1 className="text-sm text-center text-gray">
            ログインすることで、
            <a href="/terms-of-service" className="text-secondary underline">
              利用規約
            </a>
            と
            <a href="privacy-policy" className="text-secondary underline">
              プライバシーポリシー
            </a>
            に同意したものとみなされます。
          </h1>
        </div>
      </div>
    </div>
  );
};
